import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { 
  FaVolumeUp, 
  FaClock, 
  FaTrophy, 
  FaFire, 
  FaChartLine, 
  FaRedo, 
  FaHome,
  FaStar,
  FaLightbulb,
  FaCheckCircle,
  FaTimesCircle,
  FaPlay,
  FaPause,
  FaStop
} from 'react-icons/fa';

interface Question {
  id: string;
  type: 'multiple_choice' | 'translation' | 'audio' | 'matching' | 'fill_blank' | 'sentence_order';
  question: string;
  correctAnswer: string;
  options?: string[];
  audioUrl?: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  explanation?: string;
  points?: number;
  timeLimit?: number;
}

interface QuizStats {
  totalQuizzes: number;
  averageScore: number;
  bestScore: number;
  currentStreak: number;
  longestStreak: number;
  totalTimeSpent: number;
  categories: { [key: string]: number };
}

const Quiz: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [quizType, setQuizType] = useState<'quick' | 'comprehensive' | 'challenge'>('quick');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced' | 'all'>('beginner');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showQuizSetup, setShowQuizSetup] = useState(true);
  const [userStats, setUserStats] = useState<QuizStats>({
    totalQuizzes: 0,
    averageScore: 0,
    bestScore: 0,
    currentStreak: 0,
    longestStreak: 0,
    totalTimeSpent: 0,
    categories: {}
  });
  const [questionHistory, setQuestionHistory] = useState<{ [key: string]: number }>({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [streakBonus, setStreakBonus] = useState(0);

  // Enhanced question bank
  const questionBank: Question[] = [
    // Beginner Questions
    {
      id: '1',
      type: 'multiple_choice',
      question: 'معنی کلمه "سلام" چیست؟',
      correctAnswer: 'Hello',
      options: ['Hello', 'Goodbye', 'Thank you', 'Please'],
      category: 'Greetings',
      difficulty: 'beginner',
      explanation: 'سلام (salam) is the most common greeting in Persian, meaning "Hello" or "Peace".',
      points: 10,
      timeLimit: 30
    },
    {
      id: '2',
      type: 'translation',
      question: 'کلمه "Book" را به فارسی ترجمه کنید',
      correctAnswer: 'کتاب',
      category: 'Education',
      difficulty: 'beginner',
      explanation: 'کتاب (ketab) means "book" in Persian.',
      points: 15,
      timeLimit: 45
    },
    {
      id: '3',
      type: 'multiple_choice',
      question: 'کدام کلمه به معنی "آب" است؟',
      correctAnswer: 'Water',
      options: ['Fire', 'Water', 'Earth', 'Air'],
      category: 'Nature',
      difficulty: 'beginner',
      explanation: 'آب (ab) means "water" in Persian.',
      points: 10,
      timeLimit: 30
    },
    {
      id: '4',
      type: 'audio',
      question: 'این کلمه را گوش کنید و معنی آن را انتخاب کنید',
      correctAnswer: 'Family',
      options: ['Family', 'Friend', 'House', 'Food'],
      category: 'Family',
      difficulty: 'beginner',
      audioUrl: '/audio/family.mp3',
      explanation: 'خانواده (khanavade) means "family" in Persian.',
      points: 20,
      timeLimit: 60
    },
    {
      id: '5',
      type: 'fill_blank',
      question: '___ را باز کن (Open the ___)',
      correctAnswer: 'در',
      options: ['در', 'پنجره', 'کتاب', 'کیف'],
      category: 'Daily Life',
      difficulty: 'beginner',
      explanation: 'در (dar) means "door" in Persian.',
      points: 15,
      timeLimit: 45
    },
    // Intermediate Questions
    {
      id: '6',
      type: 'matching',
      question: 'کدام کلمه با "Beautiful" مطابقت دارد؟',
      correctAnswer: 'زیبا',
      options: ['زیبا', 'بزرگ', 'کوچک', 'قرمز'],
      category: 'Descriptions',
      difficulty: 'intermediate',
      explanation: 'زیبا (ziba) means "beautiful" in Persian.',
      points: 20,
      timeLimit: 40
    },
    {
      id: '7',
      type: 'sentence_order',
      question: 'جمله "من کتاب می‌خوانم" را به ترتیب صحیح بچینید',
      correctAnswer: 'من کتاب می‌خوانم',
      options: ['کتاب', 'من', 'می‌خوانم'],
      category: 'Grammar',
      difficulty: 'intermediate',
      explanation: 'The correct order is: من (I) + کتاب (book) + می‌خوانم (read).',
      points: 25,
      timeLimit: 60
    },
    // Advanced Questions
    {
      id: '8',
      type: 'translation',
      question: 'جمله "I love Persian poetry" را به فارسی ترجمه کنید',
      correctAnswer: 'من شعر فارسی را دوست دارم',
      category: 'Literature',
      difficulty: 'advanced',
      explanation: 'من شعر فارسی را دوست دارم (man sher-e farsi ra dust daram) means "I love Persian poetry".',
      points: 30,
      timeLimit: 90
    }
  ];

  useEffect(() => {
    try {
      console.log('Quiz component initializing...');
      
      // Load user stats from localStorage
      const savedStats = localStorage.getItem('quiz-stats');
      if (savedStats) {
        setUserStats(JSON.parse(savedStats));
      }

      // Load question history
      const savedHistory = localStorage.getItem('question-history');
      if (savedHistory) {
        setQuestionHistory(JSON.parse(savedHistory));
      }

      // Set loading to false after initialization
      setLoading(false);
      console.log('Quiz component loaded successfully');
    } catch (err) {
      console.error('Error loading quiz data:', err);
      setError('Failed to load quiz data. Please refresh the page.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showQuizSetup && questions.length > 0) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion.timeLimit) {
        setTimeLeft(currentQuestion.timeLimit);
        setIsTimerRunning(true);
      }
    }
  }, [currentQuestionIndex, questions, showQuizSetup]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeLeft]);

  const handleTimeUp = () => {
    setShowResult(true);
    setSelectedAnswer('');
  };

  const generateQuiz = useCallback(() => {
    let filteredQuestions = questionBank.filter(q => {
      if (difficulty !== 'all' && q.difficulty !== difficulty) return false;
      if (selectedCategory !== 'all' && q.category !== selectedCategory) return false;
      return true;
    });

    // Shuffle questions
    filteredQuestions = filteredQuestions.sort(() => Math.random() - 0.5);

    // Select questions based on quiz type
    let questionCount = 5;
    if (quizType === 'comprehensive') questionCount = 10;
    if (quizType === 'challenge') questionCount = 15;

    const selectedQuestions = filteredQuestions.slice(0, questionCount);
    setQuestions(selectedQuestions);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer('');
    setShowResult(false);
    setQuizCompleted(false);
    setShowQuizSetup(false);
    setStreakBonus(0);
  }, [quizType, difficulty, selectedCategory]);

  const handleAnswerSelect = (answer: string) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer) return;

    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const basePoints = currentQuestion.points || 10;
    const timeBonus = timeLeft > 0 ? Math.floor(timeLeft / 10) : 0;
    const totalPoints = isCorrect ? basePoints + timeBonus + streakBonus : 0;

    if (isCorrect) {
      setScore(score + totalPoints);
      setStreakBonus(prev => prev + 5);
    } else {
      setStreakBonus(0);
    }

    setIsTimerRunning(false);
    setShowResult(true);
  };

  const handleNextQuestion = () => {
    setSelectedAnswer('');
    setShowResult(false);
    setShowExplanation(false);
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      completeQuiz();
    }
  };

  const completeQuiz = () => {
    const finalScore = Math.round((score / questions.length) * 100);
    const newStats = {
      ...userStats,
      totalQuizzes: userStats.totalQuizzes + 1,
      averageScore: Math.round((userStats.averageScore * userStats.totalQuizzes + finalScore) / (userStats.totalQuizzes + 1)),
      bestScore: Math.max(userStats.bestScore, finalScore),
      currentStreak: finalScore >= 70 ? userStats.currentStreak + 1 : 0,
      longestStreak: Math.max(userStats.longestStreak, finalScore >= 70 ? userStats.currentStreak + 1 : userStats.currentStreak),
      totalTimeSpent: userStats.totalTimeSpent + (questions.reduce((acc, q) => acc + (q.timeLimit || 30), 0) - timeLeft)
    };

    setUserStats(newStats);
    localStorage.setItem('quiz-stats', JSON.stringify(newStats));
    setQuizCompleted(true);
  };

  const getScorePercentage = () => {
    return Math.round((score / questions.length) * 100);
  };

  const getScoreMessage = () => {
    const percentage = getScorePercentage();
    if (percentage >= 90) return 'عالی! شما واقعاً خوب یاد گرفته‌اید! 🎉';
    if (percentage >= 70) return 'خوب! ادامه دهید! 👍';
    if (percentage >= 50) return 'متوسط. بیشتر تمرین کنید! 💪';
    return 'نیاز به تمرین بیشتر دارید. ناامید نشوید! 📚';
  };

  const getScoreColor = () => {
    const percentage = getScorePercentage();
    if (percentage >= 90) return 'text-green-500';
    if (percentage >= 70) return 'text-blue-500';
    if (percentage >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const categories = Array.from(new Set(questionBank.map(q => q.category)));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-2xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Quiz</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (showQuizSetup) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            🎯 Quiz Center
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Choose your quiz type and challenge yourself!
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
            <div className="text-2xl font-bold text-orange-500">{userStats.totalQuizzes}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Quizzes</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
            <div className="text-2xl font-bold text-blue-500">{userStats.averageScore}%</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Average Score</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
            <div className="text-2xl font-bold text-green-500">{userStats.bestScore}%</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Best Score</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
            <div className="text-2xl font-bold text-red-500">{userStats.currentStreak}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Current Streak</div>
          </div>
        </div>

        {/* Quiz Setup */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Quiz Configuration
          </h2>

          {/* Quiz Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Quiz Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { type: 'quick', name: 'Quick Quiz', desc: '5 questions, 2-3 min', icon: '⚡' },
                { type: 'comprehensive', name: 'Comprehensive', desc: '10 questions, 5-7 min', icon: '📚' },
                { type: 'challenge', name: 'Challenge Mode', desc: '15 questions, 10-15 min', icon: '🏆' }
              ].map(({ type, name, desc, icon }) => (
                <button
                  key={type}
                  onClick={() => setQuizType(type as any)}
                  className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                    quizType === type
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-orange-300'
                  }`}
                >
                  <div className="text-2xl mb-2">{icon}</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{name}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Difficulty Level
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { level: 'all', name: 'All Levels', color: 'gray', icon: '🌟' },
                { level: 'beginner', name: 'Beginner', color: 'green', icon: '🌱' },
                { level: 'intermediate', name: 'Intermediate', color: 'yellow', icon: '🌿' },
                { level: 'advanced', name: 'Advanced', color: 'red', icon: '🌳' }
              ].map(({ level, name, color, icon }) => (
                <button
                  key={level}
                  onClick={() => setDifficulty(level as any)}
                  className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                    difficulty === level
                      ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/20`
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-2">{icon}</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Category (Optional)
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Start Quiz Button */}
          <button
            onClick={generateQuiz}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 text-lg"
          >
            🚀 Start Quiz
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          🎯 Quiz in Progress
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Question {currentQuestionIndex + 1} of {questions.length}
        </p>
      </div>

      {/* Progress and Timer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Progress
          </span>
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <FaClock className="mr-1" />
              {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </div>
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <FaTrophy className="mr-1" />
              {score} pts
            </div>
            {streakBonus > 0 && (
              <div className="flex items-center text-sm text-orange-500">
                <FaFire className="mr-1" />
                +{streakBonus}
              </div>
            )}
          </div>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Quiz Complete */}
      {quizCompleted ? (
        <div className="text-center bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Quiz Completed!
          </h2>
          <div className={`text-4xl font-bold mb-4 ${getScoreColor()}`}>
            {getScorePercentage()}%
          </div>
          <div className="text-lg text-gray-600 dark:text-gray-300 mb-6">
            {getScoreMessage()}
          </div>
          
          {/* Detailed Results */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-500">{score}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Points</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-500">{questions.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Questions</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-500">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Time Left</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowQuizSetup(true)}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
            >
              <FaRedo className="inline mr-2" />
              New Quiz
            </button>
            <Link
              to="/dashboard"
              className="px-6 py-3 border-2 border-orange-500 text-orange-500 dark:text-orange-400 font-semibold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-300"
            >
              <FaHome className="inline mr-2" />
              Dashboard
            </Link>
          </div>
        </div>
      ) : (
        /* Current Question */
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              currentQuestion.difficulty === 'beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              currentQuestion.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {currentQuestion.difficulty === 'beginner' ? '🌱 Beginner' :
               currentQuestion.difficulty === 'intermediate' ? '🌿 Intermediate' : '🌳 Advanced'}
            </span>
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
              {currentQuestion.category}
            </span>
          </div>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse mb-4">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {currentQuestion.question}
              </h3>
              {isAudioSupported() && (
                <button
                  onClick={() => playSmartAudio(undefined, currentQuestion.question).catch(console.error)}
                  className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Listen to pronunciation"
                >
                  <FaVolumeUp />
                </button>
              )}
            </div>
          </div>

          {/* Answer Options */}
          {currentQuestion.type === 'multiple_choice' && currentQuestion.options && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={showResult}
                  className={`p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                    selectedAnswer === option
                      ? showResult
                        ? option === currentQuestion.correctAnswer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-orange-300 dark:hover:border-orange-600'
                  } ${showResult ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {String.fromCharCode(65 + index)}. {option}
                    </span>
                    {isAudioSupported() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playSmartAudio(undefined, option).catch(console.error);
                        }}
                        className="p-1 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Listen to pronunciation"
                      >
                        <FaVolumeUp className="text-sm" />
                      </button>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {currentQuestion.type === 'translation' && (
            <div className="mb-8">
              <input
                type="text"
                value={selectedAnswer}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                disabled={showResult}
                dir="ltr"
                placeholder="Type your answer..."
                className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 text-left"
              />
            </div>
          )}

          {/* Result Display */}
          {showResult && (
            <div className={`p-4 rounded-lg mb-6 ${
              selectedAnswer === currentQuestion.correctAnswer
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <span className="text-2xl">
                  {selectedAnswer === currentQuestion.correctAnswer ? '✅' : '❌'}
                </span>
                <div>
                  <p className={`font-semibold ${
                    selectedAnswer === currentQuestion.correctAnswer
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {selectedAnswer === currentQuestion.correctAnswer ? 'Correct!' : 'Incorrect!'}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    Correct answer: {currentQuestion.correctAnswer}
                  </p>
                  {currentQuestion.explanation && (
                    <button
                      onClick={() => setShowExplanation(!showExplanation)}
                      className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {showExplanation ? 'Hide' : 'Show'} explanation
                    </button>
                  )}
                </div>
              </div>
              
              {showExplanation && currentQuestion.explanation && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                  <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <FaLightbulb className="text-blue-500 mt-1 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      {currentQuestion.explanation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center">
            {!showResult ? (
              <button
                onClick={handleSubmitAnswer}
                disabled={!selectedAnswer}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Answer
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-blue-600 transition-all duration-300"
              >
                {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Study Tips */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">
            Quiz Tips 📝
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="text-center">
              <div className="text-3xl mb-2">⏱️</div>
              <h4 className="font-semibold mb-2">Time Management</h4>
              <p className="text-blue-100 text-sm">
                Use your time wisely and don't rush
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔍</div>
              <h4 className="font-semibold mb-2">Read Carefully</h4>
              <p className="text-blue-100 text-sm">
                Review all options before selecting
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">📚</div>
              <h4 className="font-semibold mb-2">Learn from Mistakes</h4>
              <p className="text-blue-100 text-sm">
                Use explanations to improve
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz; 